import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useEventTracking } from '@services/statsig';

import { type OptimizationMetricContext, OptimizationModal } from './OptimizationModal';

/**
 * Hosts a single OptimizationModal for a surface and lets any descendant tile
 * open it with its metric context. One modal per surface (not one per tile);
 * the domain SemanticMetricTile calls `openOptimization` from its lever CTA.
 *
 * Owns the funnel analytics for the tile path: improve-clicked (on open),
 * booking-clicked (on book), and optimization-closed (drop-off, on close).
 *
 * The booking CTA opens the dedicated JotPsych metric-optimization Calendly.
 * The host shown on the modal's "meeting" screen is DEFAULT_SPECIALIST in
 * OptimizationModal; the host photo on the Calendly booking page itself comes
 * from the Calendly account profile (set it in Calendly, not here).
 */
const OPTIMIZATION_CALENDLY_URL =
  'https://calendly.com/d/d3wy-y99-rwb/jotpsych-metric-optimization';

type OptimizationContextValue = {
  openOptimization: (metric: OptimizationMetricContext) => void;
};

const OptimizationCtx = createContext<OptimizationContextValue | null>(null);

/**
 * Returns the opener if a host is mounted, else null. Tiles use this to show the
 * CTA only when there's actually a modal to open — no throw when a tile is
 * dispatched outside a provider.
 */
export const useOptionalOptimization = (): OptimizationContextValue | null =>
  useContext(OptimizationCtx);

export const OptimizationModalProvider: React.FC<{
  children: React.ReactNode;
  /** Which surface hosts this modal — stamped on the funnel events. */
  surface?: 'home' | 'dashboard';
  organizationId?: string;
}> = ({ children, surface, organizationId }) => {
  const { practiceMetrics } = useEventTracking();
  const [metric, setMetric] = useState<OptimizationMetricContext | null>(null);
  const [open, setOpen] = useState(false);

  const openOptimization = useCallback(
    (m: OptimizationMetricContext) => {
      // The lever click that opens the funnel — the intent signal.
      practiceMetrics.improveClicked({
        organization_id: organizationId,
        metric_slug: m.analytics?.slug,
        surface,
        value_pct: m.analytics?.valuePct ?? null,
        target: m.analytics?.targetPct ?? null,
        gap: m.analytics?.gap ?? null,
      });
      setMetric(m);
      setOpen(true);
    },
    [practiceMetrics, organizationId, surface],
  );

  const value = useMemo(() => ({ openOptimization }), [openOptimization]);

  return (
    <OptimizationCtx.Provider value={value}>
      {children}
      {metric && (
        <OptimizationModal
          // Remount per metric so transient modal state (e.g. `booked`) never
          // leaks into a different tile opened within the close animation window.
          key={metric.analytics?.slug}
          open={open}
          onClose={reachedBooking => {
            practiceMetrics.optimizationClosed({
              organization_id: organizationId,
              metric_slug: metric.analytics?.slug,
              surface,
              reached_booking: reachedBooking,
            });
            setOpen(false);
          }}
          metric={metric}
          onBook={() => {
            practiceMetrics.optimizationBookingClicked({
              organization_id: organizationId,
              metric_slug: metric.analytics?.slug,
              surface,
              target: metric.analytics?.targetPct ?? null,
              gap: metric.analytics?.gap ?? null,
            });
            window.open(OPTIMIZATION_CALENDLY_URL, '_blank', 'noopener,noreferrer');
          }}
        />
      )}
    </OptimizationCtx.Provider>
  );
};
